import type { AccountSummary } from '@fruitful/types';
import React from 'react';
import {
    ArrowDownRightIcon,
    BodyLargeEmphasized,
    BodyMediumEmphasized,
    BodyMediumRegular,
    H5Emphasized,
    H6Emphasized,
    Icon,
    View,
    XStack,
    YStack,
} from 'smoothie';

import { formatDollars } from '../../../../common/utils/formatting';
import {
    type PaycheckRoutingRow,
    type PaycheckRoutingRule,
    PaycheckRoutingRuleType,
} from '../../../entityTypes';

const InfoRow = ({
    label,
    value,
}: {
    label: string;
    value: string | number;
}) => {
    return (
        <XStack gap={8}>
            <BodyMediumEmphasized>{label}:</BodyMediumEmphasized>
            <BodyMediumRegular>{value}</BodyMediumRegular>
        </XStack>
    );
};

const Rule = ({
    rule,
    membershipAccounts,
}: {
    rule: PaycheckRoutingRule;
    membershipAccounts: AccountSummary[];
}) => {
    const { accountId, amount, type } = rule;

    const action =
        type === PaycheckRoutingRuleType.MINIMUM_BALANCE
            ? 'Replenish'
            : 'Transfer';
    const formattedAmount =
        type === PaycheckRoutingRuleType.PERCENTAGE
            ? `${amount}%`
            : formatDollars(amount);
    const account = membershipAccounts.find(
        (account) => account.bankAccountId === accountId,
    );

    return (
        <XStack alignItems="center" gap={4}>
            <Icon icon={ArrowDownRightIcon} />
            <XStack>
                <BodyMediumRegular>{action} </BodyMediumRegular>
                <BodyMediumEmphasized>{formattedAmount}</BodyMediumEmphasized>
                <BodyMediumRegular> to </BodyMediumRegular>
                <BodyMediumEmphasized>
                    {account?.name ?? 'N/A'}
                </BodyMediumEmphasized>
            </XStack>
        </XStack>
    );
};

interface PaycheckSplitRulesModalProps {
    paycheckSplitDetails: PaycheckRoutingRow | null;
    membershipAccounts: AccountSummary[];
}

const PaycheckSplitRulesModal = ({
    paycheckSplitDetails,
    membershipAccounts,
}: PaycheckSplitRulesModalProps) => {
    if (!paycheckSplitDetails) return null;

    const {
        originator,
        receiver,
        institution,
        account,
        amount,
        status,
        rules,
    } = paycheckSplitDetails;

    return (
        <YStack gap={20} width="90vw" $sm={{ width: 400 }}>
            <H5Emphasized>Paycheck Split Rules</H5Emphasized>
            <YStack
                gap={4}
                borderRadius={8}
                backgroundColor="$gray200"
                padding={12}
            >
                <H6Emphasized>Paycheck Info:</H6Emphasized>
                <XStack justifyContent="space-between">
                    <YStack gap={4}>
                        <InfoRow label="Originator" value={originator} />
                        <InfoRow label="Receiver" value={receiver} />
                        <InfoRow label="Status" value={status} />
                    </YStack>
                    <YStack gap={4}>
                        <InfoRow label="Institution" value={institution} />
                        <InfoRow label="Account" value={account} />
                        <InfoRow label="Amount" value={amount} />
                    </YStack>
                </XStack>
            </YStack>
            {status === 'ELIGIBLE' ? (
                <View borderRadius={8} padding={12} backgroundColor="$red300">
                    <BodyMediumRegular>No rules configured</BodyMediumRegular>
                </View>
            ) : (
                <YStack
                    gap={8}
                    borderRadius={8}
                    padding={12}
                    backgroundColor="$green300"
                >
                    <H6Emphasized>Rules</H6Emphasized>
                    {(rules?.primary ?? []).length > 0 && (
                        <YStack>
                            <BodyLargeEmphasized>Primary:</BodyLargeEmphasized>
                            <YStack>
                                {rules?.primary.map((rule, i) => {
                                    return (
                                        <Rule
                                            key={i}
                                            rule={rule}
                                            membershipAccounts={
                                                membershipAccounts
                                            }
                                        />
                                    );
                                })}
                            </YStack>
                        </YStack>
                    )}
                    {(rules?.remaining ?? []).length > 0 && (
                        <YStack>
                            <BodyLargeEmphasized>
                                Remaining:
                            </BodyLargeEmphasized>
                            <YStack>
                                {rules?.remaining.map((rule, i) => {
                                    return (
                                        <Rule
                                            key={i}
                                            rule={rule}
                                            membershipAccounts={
                                                membershipAccounts
                                            }
                                        />
                                    );
                                })}
                            </YStack>
                        </YStack>
                    )}
                </YStack>
            )}
        </YStack>
    );
};

export default PaycheckSplitRulesModal;
